import { useEffect, useMemo, useRef, useState } from 'react';
import { srgbToLinear, toLinearLighting } from '../../render/lighting';
import { toRelativeNodeSizes } from '../graph/nodeSizes';
import { buildNetworkLayout } from '../layout/buildNetworkLayout';
import { NETWORK_CAMERA } from '../networkViewSettings';
import { fitNetworkZoom } from '../render/fitNetworkZoom';
import {
  NO_AMBIENT_OCCLUSION,
  type NetworkTheme,
} from '../render/networkTheme';
import { packNetworkInstances } from '../render/packNetwork';
import { useGpuCanvas } from '../render/useGpuCanvas';
import { STATIC_NETWORK_FLOW } from '../render/webgpu/networkUniforms';
import {
  createNetworkGpuRenderer,
  type NetworkGpuRenderer,
} from '../render/webgpu/networkRenderer';
import type { NetworkGraph } from '../types';

/**
 * A thumbnail of a graph, drawn with the renderer the view beside it is already using.
 *
 * Exists for one caller: the tooltip over a collapsed group, which has to answer "what is in
 * there" and can answer it with the thing itself rather than with a count. A number tells a
 * reader how much was folded away; a picture tells them whether it is a chain, a star or a mesh,
 * which is the question a collapse makes it impossible to see any other way.
 *
 * `device` is the parent's, and passing it rather than acquiring one is the whole reason this is
 * a component and not a second `Network3DView`. A device is an asynchronous request against the
 * adapter and holds driver resources that have to be destroyed; mounting a view that owns one
 * inside a tooltip would request and destroy a device per hover, which is both slow enough to see
 * and a leak whenever the pointer moves on before the adapter answers. Sharing costs nothing -
 * a device is designed for many canvases - and the renderer built on it is torn down with this
 * component in the ordinary way.
 *
 * Deliberately not interactive: no orbit, no hit-testing, no fit latch. It sits under a tooltip
 * that sets `pointer-events: none`, so there is no gesture that could reach it, and every dial
 * that exists to let a reader take the camera would be dead code here.
 */
export type NetworkMiniatureProps = {
  graph: NetworkGraph;
  theme: NetworkTheme;
  device: GPUDevice;
  width: number;
  height: number;
  /** Seeded like the host view's, so the same subgraph draws the same way each time it is opened. */
  seed: number;
};

/**
 * Fewer than the full view's 320.
 *
 * A subgraph is tens of nodes rather than hundreds, and a force solve settles in iterations
 * proportional to what it has to untangle. This runs inside a pointer-move handler's render, so
 * the budget is a frame rather than the second the full view can spend on mount.
 */
const MINIATURE_ITERATIONS = 90;

/**
 * Thicker marks and edges than the full view's.
 *
 * The thumbnail is an eighth of the linear size of the pane, and a scale that is right at 900
 * pixels across draws sub-pixel at 160: the nodes vanish and the references alias into a grey
 * haze. These are the full view's values taken up until the picture reads at the size it is
 * actually drawn, which is what a thumbnail is for.
 */
const MINIATURE_NODE_SCALE = 0.16;
const MINIATURE_EDGE_WIDTH = 1.4;
const MINIATURE_EDGE_OPACITY = 0.7;

/**
 * Margin around the thumbnail, in pixels, which is not the full view's.
 *
 * `NETWORK_FIT_INSET` is 18 and 34, chosen against a pane most of a thousand pixels tall. Applied
 * to a box 140 tall it reserves half the height, and the graph lands in the middle third of the
 * thumbnail as a handful of specks. The insets cover the largest mark's own radius, which scales
 * with the box rather than being a constant - so a box an eighth the size wants an inset about an
 * eighth the size.
 */
const MINIATURE_FIT_INSET = { horizontal: 4, vertical: 6 };

export const NetworkMiniature = ({
  graph,
  theme,
  device,
  width,
  height,
  seed,
}: NetworkMiniatureProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { containerRef, viewport, deviceViewport, pixelRatio } =
    useGpuCanvas(canvasRef);

  const nodeSizes = useMemo(
    () => toRelativeNodeSizes(graph.nodes),
    [graph.nodes]
  );

  /* Force, always, whatever the host view is drawing. The subgraph arrives without the grouping
     that produced it - it is the contents of one group - so the layouts that arrange by structure
     have no structure to arrange by, and the question a thumbnail answers is what connects to
     what. */
  const layout = useMemo(
    () =>
      buildNetworkLayout({
        graph,
        kind: 'force',
        seed,
        iterations: MINIATURE_ITERATIONS,
        bundleStrength: 0,
        zStretch: 1,
        nodeSizes,
      }),
    [graph, seed, nodeSizes]
  );

  const instances = useMemo(
    () =>
      packNetworkInstances({
        graph,
        layout,
        theme,
        focus: null,
        nodeSizes,
        nodeScale: MINIATURE_NODE_SCALE,
        edgeWidth: MINIATURE_EDGE_WIDTH,
        guideWidth: 1,
        edgeOpacity: MINIATURE_EDGE_OPACITY,
        groupCloudDensity: 0,
      }),
    [graph, layout, theme, nodeSizes]
  );

  const camera = useMemo(
    () => ({
      ...NETWORK_CAMERA,
      zoom:
        NETWORK_CAMERA.zoom *
        fitNetworkZoom({
          camera: NETWORK_CAMERA,
          viewport,
          layout,
          inset: MINIATURE_FIT_INSET,
        }),
    }),
    [viewport, layout]
  );

  const linearLighting = useMemo(
    () => toLinearLighting(theme.lighting),
    [theme.lighting]
  );
  const linearSurface = useMemo(
    (): readonly [number, number, number] => [
      srgbToLinear(theme.surface[0]),
      srgbToLinear(theme.surface[1]),
      srgbToLinear(theme.surface[2]),
    ],
    [theme.surface]
  );

  const [renderer, setRenderer] = useState<NetworkGpuRenderer | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const created = createNetworkGpuRenderer({ device, canvas });
    setRenderer(created);

    return () => {
      setRenderer(null);
      created.destroy();
    };
  }, [device]);

  useEffect(() => {
    renderer?.setInstances(instances);
  }, [renderer, instances]);

  useEffect(() => {
    if (!renderer) return;
    /* On `viewport` rather than `deviceViewport`, which is floored at one pixel - see the full
       view's draw effect for what a resolve-target mismatch costs. */
    if (viewport.width === 0 || viewport.height === 0) return;

    let pendingFrame: number | undefined;
    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = undefined;
      renderer.draw({
        camera,
        viewport: deviceViewport,
        pixelRatio,
        lighting: linearLighting,
        surface: linearSurface,
        /* Off at thumbnail size, where the sampling radius covers most of the picture and the
           occlusion textures would be held for as long as the miniature lives. */
        ambientOcclusion: NO_AMBIENT_OCCLUSION,
        edgeBlend: theme.edgeBlend,
        /* Still. A thumbnail is a picture of what is inside a closed group, drawn once, and
           nothing in it is focused for a pulse to belong to. */
        flow: STATIC_NETWORK_FLOW,
      });
    });

    return () => {
      if (pendingFrame !== undefined) cancelAnimationFrame(pendingFrame);
    };
  }, [
    renderer,
    instances,
    camera,
    viewport,
    deviceViewport,
    pixelRatio,
    linearLighting,
    linearSurface,
    theme.edgeBlend,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        width,
        height,
        borderRadius: 4,
        overflow: 'hidden',
        background: theme.css.surface,
      }}
    >
      {/* Hidden rather than named. The tooltip around it already carries the group's name and
          what is behind it; the picture adds the shape of the coupling, which has no short text
          equivalent - so an accessible name here could only repeat the line above it. */}
      <canvas ref={canvasRef} aria-hidden="true" />
    </div>
  );
};
